'use client';

import { useEffect, useRef, useState } from 'react';
import { Arrow, CalendarMark, MonthGrid, dayLabel, dayOf, isoDay, monthOf } from './month-grid';

/** One day at a time: ‹ and › walk, the date opens a month, Today jumps back,
 *  and after today is drawn quiet because there is nothing to read there. The
 *  browser's own input looked different in every browser and had no "Today". */
export function DayStepper({
  value,
  onChange,
  label = 'Day',
}: {
  /** Local `YYYY-MM-DD`. */
  value: string;
  onChange: (day: string) => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => monthOf(value));
  const box = useRef<HTMLDivElement>(null);
  const today = isoDay(new Date());

  useEffect(() => {
    if (open) setMonth(monthOf(value));
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const step = (by: number) => {
    const at = dayOf(value) ?? new Date();
    const next = isoDay(new Date(at.getFullYear(), at.getMonth(), at.getDate() + by));
    if (next <= today) onChange(next);
  };

  return (
    <div className="ds" ref={box}>
      <div className="ds-bar" role="group" aria-label={label}>
        <button type="button" className="ds-step" aria-label="Previous day" onClick={() => step(-1)}>
          <Arrow way="back" />
        </button>
        <button
          type="button"
          className="ds-face"
          aria-expanded={open}
          aria-label={`${label}: ${dayLabel(value, true)}`}
          onClick={() => setOpen((was) => !was)}
        >
          <CalendarMark />
          {dayLabel(value, true)}
        </button>
        <button
          type="button"
          className="ds-step"
          aria-label="Next day"
          disabled={value >= today}
          onClick={() => step(1)}
        >
          <Arrow way="on" />
        </button>
      </div>
      <button type="button" className="btn btn-sm ds-today" disabled={value === today} onClick={() => onChange(today)}>
        {'Today'}
      </button>

      {open && (
        <div className="ds-pop" role="dialog" aria-label={`Choose ${label.toLowerCase()}`}>
          <MonthGrid
            month={month}
            onMonth={setMonth}
            from={value}
            to={value}
            max={today}
            onPick={(day) => {
              onChange(day);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
